
import React, { useState, useEffect } from 'react'
import { useAuth } from '@/hooks/useAuth'
import { getCurrentUserSubordinates } from '../../../lib/real-data-service'
import { 
  Calendar,
  Clock,
  Users,
  Plus,
  Search,
  Filter,
  MapPin,
  Video,
  Phone,
  MessageSquare,
  CheckCircle,
  AlertCircle,
  User,
  Edit,
  Trash2,
  Send,
  ChevronLeft,
  ChevronRight,
  Eye
} from 'lucide-react'

export default function MeetingSchedulePage() {
  const { user } = useAuth()
  const [selectedDate, setSelectedDate] = useState(new Date())
  const [viewMode, setViewMode] = useState<'calendar' | 'list'>('calendar')
  const [meetingType, setMeetingType] = useState<'1on1' | 'performance' | 'team' | 'onboarding'>('1on1')
  const [showNewMeeting, setShowNewMeeting] = useState(false)

  // Loading states
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  // Data states
  const [subordinates, setSubordinates] = useState<any[]>([])
  const [meetings, setMeetings] = useState<any[]>([])

  // Load subordinates and generate meetings
  useEffect(() => {
    const loadData = async () => {
      try {
        setLoading(true)
        setError(null)

        if (!user?.employee?.email) {
          setError('User not authenticated or employee data not available')
          setLoading(false)
          return
        }

        // Get real employee data for meeting attendees
        const userSubordinates = await getCurrentUserSubordinates(user.employee.email)
        setSubordinates(userSubordinates)

        // Generate sample meetings based on real employees
        const generatedMeetings = userSubordinates.slice(0, 3).map((emp: any, index: number) => ({
          id: index + 1,
          title: `1:1 com ${emp.name}`,
          type: '1on1' as const,
          date: new Date(Date.now() + (index + 1) * 24 * 60 * 60 * 1000).toISOString().split('T')[0], // Next few days
          time: `${14 + index}:00`,
          duration: 60,
          location: `Sala de Reunião ${index + 1}`,
          attendees: [emp.name],
          status: 'confirmed' as const,
          agenda: ['Feedback do projeto atual', 'Metas do próximo trimestre', 'PDI - desenvolvimento técnico']
        }))

        // Add sample meetings if no subordinates
        if (generatedMeetings.length === 0) {
          setMeetings([{
            id: 1,
            title: '1:1 - Reunião de Exemplo',
            type: '1on1' as const,
            date: '2025-02-16',
            time: '10:00',
            duration: 90,
            location: 'Online (Microsoft Teams)',
            attendees: ['Exemplo'],
            status: 'confirmed' as const,
            agenda: ['Revisão de metas 2024', 'Avaliação 360°', 'Plano de desenvolvimento 2025']
          }])
        } else {
          setMeetings(generatedMeetings)
        }
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Failed to load meeting data')
        console.error('Error loading meeting data:', err)
      } finally {
        setLoading(false)
      }
    }

    loadData()
  }, [])
  
  // This is now handled in useEffect

  const [newMeeting, setNewMeeting] = useState({
    title: '',
    type: '1on1' as const,
    date: '',
    time: '',
    duration: 60,
    location: '',
    attendees: [] as string[],
    agenda: [''],
    description: ''
  })

  // Mock employee data for attendee selection
  const employees = [
    { id: 'emp1', name: 'Ana Santos', department: 'Criação', position: 'Designer' },
    { id: 'emp2', name: 'Carlos Silva', department: 'Estratégia', position: 'Diretor' },
    { id: 'emp3', name: 'Marina Costa', department: 'Produção', position: 'Produtora' },
    { id: 'emp4', name: 'João Oliveira', department: 'Criação', position: 'Designer Jr.' },
    { id: 'emp5', name: 'Patricia Lima', department: 'Atendimento', position: 'Account Manager' }
  ]

  const handleCreateMeeting = () => {
    setShowNewMeeting(false)
    setNewMeeting({
      title: '',
      type: '1on1',
      date: '',
      time: '',
      duration: 60,
      location: '',
      attendees: [],
      agenda: [''],
      description: ''
    })
  }

  const addAgendaItem = () => {
    setNewMeeting({
      ...newMeeting,
      agenda: [...newMeeting.agenda, '']
    })
  }

  const updateAgendaItem = (index: number, value: string) => {
    const updatedAgenda = [...newMeeting.agenda]
    updatedAgenda[index] = value
    setNewMeeting({
      ...newMeeting,
      agenda: updatedAgenda
    })
  }

  const removeAgendaItem = (index: number) => {
    if (newMeeting.agenda.length > 1) {
      const updatedAgenda = newMeeting.agenda.filter((_, i) => i !== index)
      setNewMeeting({
        ...newMeeting,
        agenda: updatedAgenda
      })
    }
  }

  const getMeetingTypeColor = (type: string) => {
    switch (type) {
      case '1on1': return 'bg-brand-primary text-brand-dark'
      case 'performance': return 'bg-blue-600 text-white'
      case 'team': return 'bg-purple-600 text-white'
      case 'onboarding': return 'bg-green-600 text-white'
      default: return 'bg-gray-600 text-white'
    }
  }

  const getMeetingTypeLabel = (type: string) => {
    switch (type) {
      case '1on1': return '1:1'
      case 'performance': return 'Performance'
      case 'team': return 'Equipe'
      case 'onboarding': return 'Onboarding'
      default: return type
    }
  }

  const renderNewMeetingForm = () => (
    <div className="modal-overlay">
      <div className="modal-content w-full max-w-2xl max-h-[90vh] overflow-y-auto animate-scale-in">
        <div className="flex items-center justify-between mb-6">
          <h2 className="font-display text-2xl font-bold text-white">
            Agendar Nova Reunião
          </h2>
          <button
            onClick={() => setShowNewMeeting(false)}
            className="btn-icon text-brand-gray hover:text-white"
          >
            ×
          </button>
        </div>

        <div className="space-y-6">
          {/* Basic Info */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="form-label">
                Título da Reunião
              </label>
              <input
                type="text"
                value={newMeeting.title}
                onChange={(e) => setNewMeeting({...newMeeting, title: e.target.value})}
                className="form-input"
                placeholder="Ex: 1:1 com Ana Santos"
              />
            </div>

            <div>
              <label className="form-label">
                Tipo de Reunião
              </label>
              <select
                value={newMeeting.type}
                onChange={(e) => setNewMeeting({...newMeeting, type: e.target.value as any})}
                className="form-select"
              >
                <option value="1on1">1:1</option>
                <option value="performance">Avaliação de Performance</option>
                <option value="team">Reunião de Equipe</option>
                <option value="onboarding">Onboarding</option>
              </select>
            </div>
          </div>

          {/* Date and Time */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div>
              <label className="form-label">
                Data
              </label>
              <input
                type="date"
                value={newMeeting.date}
                onChange={(e) => setNewMeeting({...newMeeting, date: e.target.value})}
                className="form-input"
              />
            </div>

            <div>
              <label className="form-label">
                Horário
              </label>
              <input
                type="time"
                value={newMeeting.time}
                onChange={(e) => setNewMeeting({...newMeeting, time: e.target.value})}
                className="form-input"
              />
            </div>

            <div>
              <label className="form-label">
                Duração (min)
              </label>
              <select
                value={newMeeting.duration}
                onChange={(e) => setNewMeeting({...newMeeting, duration: parseInt(e.target.value)})}
                className="form-select"
              >
                <option value="30">30 min</option>
                <option value="45">45 min</option>
                <option value="60">1 hora</option>
                <option value="90">1h30</option>
                <option value="120">2 horas</option>
              </select>
            </div>
          </div>

          {/* Location */}
          <div>
            <label className="form-label">
              Local / Link
            </label>
            <input
              type="text"
              value={newMeeting.location}
              onChange={(e) => setNewMeeting({...newMeeting, location: e.target.value})}
              className="form-input"
              placeholder="Ex: Sala de Reunião 1 ou https://teams.microsoft.com/..."
            />
          </div>

          {/* Attendees */}
          <div>
            <label className="form-label">
              Participantes
            </label>
            <div className="space-y-2 max-h-32 overflow-y-auto">
              {employees.map((employee) => (
                <label key={employee.id} className="flex items-center">
                  <input
                    type="checkbox"
                    checked={newMeeting.attendees.includes(employee.name)}
                    onChange={(e) => {
                      if (e.target.checked) {
                        setNewMeeting({
                          ...newMeeting,
                          attendees: [...newMeeting.attendees, employee.name]
                        })
                      } else {
                        setNewMeeting({
                          ...newMeeting,
                          attendees: newMeeting.attendees.filter(name => name !== employee.name)
                        })
                      }
                    }}
                    className="mr-3"
                  />
                  <div>
                    <span className="text-white">{employee.name}</span>
                    <span className="text-brand-gray text-sm ml-2">
                      {employee.position} • {employee.department}
                    </span>
                  </div>
                </label>
              ))}
            </div>
          </div>

          {/* Agenda */}
          <div>
            <div className="flex items-center justify-between mb-2">
              <label className="form-label">
                Agenda
              </label>
              <button
                onClick={addAgendaItem}
                className="btn-ghost text-sm flex items-center gap-1"
              >
                <Plus size={16} />
                Adicionar Item
              </button>
            </div>
            <div className="space-y-2">
              {newMeeting.agenda.map((item, index) => (
                <div key={index} className="flex items-center space-x-2">
                  <input
                    type="text"
                    value={item}
                    onChange={(e) => updateAgendaItem(index, e.target.value)}
                    className="form-input flex-1"
                    placeholder={`Item ${index + 1} da agenda`}
                  />
                  {newMeeting.agenda.length > 1 && (
                    <button
                      onClick={() => removeAgendaItem(index)}
                      className="btn-icon text-red-400 hover:text-red-300"
                    >
                      <Trash2 size={16} />
                    </button>
                  )}
                </div>
              ))}
            </div>
          </div>

          {/* Description */}
          <div>
            <label className="form-label">
              Descrição / Observações
            </label>
            <textarea
              value={newMeeting.description}
              onChange={(e) => setNewMeeting({...newMeeting, description: e.target.value})}
              className="form-textarea"
              rows={3}
              placeholder="Informações adicionais sobre a reunião..."
            />
          </div>

          {/* Actions */}
          <div className="flex justify-end space-x-4">
            <button
              onClick={() => setShowNewMeeting(false)}
              className="btn-ghost"
            >
              Cancelar
            </button>
            <button
              onClick={handleCreateMeeting}
              className="btn-primary flex items-center gap-2 group"
            >
              <Calendar size={16} className="transition-transform duration-200 group-hover:scale-110" />
              Agendar Reunião
            </button>
          </div>
        </div>
      </div>
    </div>
  )

  const renderCalendarView = () => (
    <div className="card-hover animate-fade-in-up">
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center space-x-4">
          <button className="text-brand-gray hover:text-white">
            <ChevronLeft size={20} />
          </button>
          <h3 className="text-white font-medium text-lg">Fevereiro 2025</h3>
          <button className="text-brand-gray hover:text-white">
            <ChevronRight size={20} />
          </button>
        </div>
        
        <div className="flex items-center space-x-2">
          <button
            onClick={() => setViewMode('list')}
            className={`px-3 py-2 rounded-lg transition-colors ${
              viewMode === 'list' ? 'bg-brand-primary text-brand-dark' : 'text-brand-gray hover:text-white'
            }`}
          >
            Lista
          </button>
          <button
            onClick={() => setViewMode('calendar')}
            className={`px-3 py-2 rounded-lg transition-colors ${
              viewMode === 'calendar' ? 'bg-brand-primary text-brand-dark' : 'text-brand-gray hover:text-white'
            }`}
          >
            Calendário
          </button>
        </div>
      </div>

      {/* Calendar Grid */}
      <div className="grid grid-cols-7 gap-2 mb-4">
        {['Dom', 'Seg', 'Ter', 'Qua', 'Qui', 'Sex', 'Sáb'].map((day) => (
          <div key={day} className="text-center text-brand-gray font-medium py-2">
            {day}
          </div>
        ))}
        
        {/* Calendar days - simplified for demo */}
        {Array.from({ length: 35 }, (_, i) => {
          const day = i - 5 + 1 // Start from Feb 1st
          const isCurrentMonth = day > 0 && day <= 28
          const hasEvent = [15, 16, 17, 18].includes(day)
          
          return (
            <div
              key={i}
              className={`min-h-[100px] p-2 border border-gray-700 rounded-lg ${
                isCurrentMonth ? 'bg-brand-dark' : 'bg-gray-800'
              }`}
            >
              {isCurrentMonth && (
                <>
                  <div className={`text-sm ${hasEvent ? 'text-brand-primary font-bold' : 'text-white'}`}>
                    {day}
                  </div>
                  {hasEvent && (
                    <div className="mt-1 space-y-1">
                      {meetings
                        .filter(m => new Date(m.date).getDate() === day)
                        .map(meeting => (
                          <div
                            key={meeting.id}
                            className={`text-xs p-1 rounded ${getMeetingTypeColor(meeting.type)} cursor-pointer`}
                          >
                            {meeting.time} - {meeting.title.slice(0, 15)}...
                          </div>
                        ))}
                    </div>
                  )}
                </>
              )}
            </div>
          )
        })}
      </div>
    </div>
  )

  const renderListView = () => (
    <div className="space-y-4 animate-fade-in-up">
      {meetings.map((meeting) => (
        <div key={meeting.id} className="card-hover">
          <div className="flex items-start justify-between">
            <div className="flex-1">
              <div className="flex items-center space-x-3 mb-3">
                <span className={`px-3 py-1 rounded-full text-xs font-medium ${getMeetingTypeColor(meeting.type)}`}>
                  {getMeetingTypeLabel(meeting.type)}
                </span>
                <span className={`px-2 py-1 rounded-full text-xs ${
                  meeting.status === 'confirmed' ? 'bg-green-900 text-green-300' : 'bg-yellow-900 text-yellow-300'
                }`}>
                  {meeting.status === 'confirmed' ? 'Confirmado' : 'Pendente'}
                </span>
              </div>
              
              <h3 className="text-white font-medium text-lg mb-2">{meeting.title}</h3>
              
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
                <div className="flex items-center text-brand-gray">
                  <Calendar size={16} className="mr-2" />
                  {new Date(meeting.date).toLocaleDateString('pt-BR')}
                </div>
                <div className="flex items-center text-brand-gray">
                  <Clock size={16} className="mr-2" />
                  {meeting.time} ({meeting.duration}min)
                </div>
                <div className="flex items-center text-brand-gray">
                  <MapPin size={16} className="mr-2" />
                  {meeting.location}
                </div>
              </div>

              {meeting.attendees.length > 0 && (
                <div className="mb-4">
                  <p className="text-brand-gray text-sm mb-2">Participantes:</p>
                  <div className="flex items-center space-x-2">
                    {meeting.attendees.map((attendee: string, index: number) => (
                      <div key={index} className="flex items-center bg-brand-dark px-3 py-1 rounded-full">
                        <User size={14} className="text-brand-primary mr-2" />
                        <span className="text-white text-sm">{attendee}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {meeting.agenda.length > 0 && (
                <div>
                  <p className="text-brand-gray text-sm mb-2">Agenda:</p>
                  <ul className="space-y-1">
                    {meeting.agenda.map((item: string, index: number) => (
                      <li key={index} className="text-white text-sm flex items-start">
                        <span className="text-brand-primary mr-2">•</span>
                        {item}
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </div>

            <div className="flex items-center space-x-2 ml-4">
              <button className="btn-icon text-brand-primary hover:text-white">
                <Eye size={20} />
              </button>
              <button className="btn-icon text-brand-primary hover:text-white">
                <Edit size={20} />
              </button>
              <button className="btn-icon text-red-400 hover:text-red-300">
                <Trash2 size={20} />
              </button>
            </div>
          </div>
        </div>
      ))}
    </div>
  )

  // Loading state
  if (loading) {
    return (
      
        <div className="space-y-8">
          <div className="text-center py-12">
            <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-brand-primary mb-4"></div>
            <p className="text-white text-lg">Carregando agenda de reuniões...</p>
          </div>
        </div>
      
    )
  }

  // Error state
  if (error) {
    return (
      
        <div className="space-y-8">
          <div className="text-center py-12">
            <div className="text-red-400 text-lg mb-2">Erro ao carregar dados</div>
            <p className="text-brand-gray mb-4">{error}</p>
            <button
              onClick={() => window.location.reload()}
              className="btn-primary"
            >
              Tentar Novamente
            </button>
          </div>
        </div>
      
    )
  }

  return (
    
      <div className="space-y-8">
        {/* Header */}
        <div className="flex flex-col lg:flex-row lg:justify-between lg:items-center space-y-4 lg:space-y-0">
          <div>
            <h1 className="font-display text-3xl sm:text-4xl font-bold text-white mb-2 bg-gradient-to-r from-white to-brand-gray-light bg-clip-text text-transparent">
              Agenda de Reuniões
            </h1>
            <p className="text-brand-gray-light font-body text-lg">
              Gerencie 1:1s, avaliações de performance e reuniões de equipe
            </p>
          </div>
          
          <button
            onClick={() => setShowNewMeeting(true)}
            className="btn-primary flex items-center gap-2 group"
          >
            <Plus size={16} className="transition-transform duration-200 group-hover:rotate-90" />
            Nova Reunião
          </button>
        </div>

        {/* Meeting Type Filter */}
        <div className="card-hover">
          <div className="flex flex-col sm:flex-row sm:items-center space-y-3 sm:space-y-0 sm:space-x-4">
            <span className="text-brand-gray font-medium font-display">Filtrar por tipo:</span>
            <div className="flex flex-wrap gap-2">
              {['all', '1on1', 'performance', 'team', 'onboarding'].map((type) => (
                <button
                  key={type}
                  onClick={() => setMeetingType(type as any)}
                  className={`px-4 py-2 rounded-monumenta transition-all duration-300 font-display font-semibold ${
                    meetingType === type || (type === 'all' && !meetingType)
                      ? 'bg-brand-primary text-brand-dark shadow-monumenta-brand'
                      : 'text-brand-gray hover:text-white hover:bg-brand-dark-lighter'
                  }`}
                >
                  {type === 'all' ? 'Todos' : getMeetingTypeLabel(type)}
                </button>
              ))}
            </div>
          </div>
        </div>

        {/* Meeting Stats */}
        <div className="dashboard-grid md:grid-cols-4">
          <div className="metric-card">
            <div className="flex items-center justify-between mb-2">
              <Calendar className="text-brand-primary" size={20} />
            </div>
            <div className="metric-value">12</div>
            <div className="metric-label">Total do Mês</div>
          </div>

          <div className="metric-card">
            <div className="flex items-center justify-between mb-2">
              <Users className="text-brand-blue" size={20} />
            </div>
            <div className="metric-value">5</div>
            <div className="metric-label">1:1s Pendentes</div>
          </div>

          <div className="metric-card">
            <div className="flex items-center justify-between mb-2">
              <CheckCircle className="text-success" size={20} />
            </div>
            <div className="metric-value">3</div>
            <div className="metric-label">Avaliações</div>
          </div>

          <div className="metric-card">
            <div className="flex items-center justify-between mb-2">
              <Clock className="text-warning" size={20} />
            </div>
            <div className="metric-value">75min</div>
            <div className="metric-label">Tempo Médio</div>
          </div>
        </div>

        {/* Calendar/List View */}
        {viewMode === 'calendar' ? renderCalendarView() : renderListView()}

        {/* New Meeting Modal */}
        {showNewMeeting && renderNewMeetingForm()}
      </div>
    
  )
} 